import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Trophy,
  Timer,
  User,
  Users,
  ChevronDown,
  BookOpen,
  RotateCcw,
  LogOut,
  Link2,
  Info,
} from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import GameMenu from "@/games/shared/GameMenu";
import PlayerSetup from "@/games/shared/PlayerSetup";
import Scoreboard from "@/games/shared/Scoreboard";
import TurnTimer from "@/games/shared/TurnTimer";
import MatchResultDialog from "@/games/shared/MatchResultDialog";
import { useHotSeatMatch, type PlayerId } from "@/games/shared/useHotSeatMatch";
import { play } from "@/games/shared/sound";
import { LiveAnnouncer, useAnnouncer } from "@/games/shared/LiveAnnouncer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  generateQuestion,
  scoreAnswer,
  SNAP_FAST_THRESHOLD_S,
  SNAP_POINTS_FAST,
  SNAP_POINTS_SLOW,
  SNAP_TURN_SECONDS,
  type SnapOutcome,
  type SnapQuestion,
} from "@/games/bondTypeSnap/engine";
import { BOND_LABEL, type BondType } from "@/games/bondTypeSnap/compounds";

const QUESTIONS_PER_MATCH = 15;
const RESULT_PAUSE_MS = 1900;
const SOLO_BEST_KEY = "elementpad:bond-type-snap:best";

type Mode = "solo" | "hot-seat";

type HistoryEntry = {
  id: string;
  name: string;
  formula: string;
  bond: BondType;
  why: string;
  note?: string;
  outcome: SnapOutcome;
};

const BOND_OPTIONS: BondType[] = ["ionic", "covalent", "metallic"];

const readBest = (): number => {
  try {
    const raw = window.localStorage.getItem(SOLO_BEST_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
};
const writeBest = (score: number) => {
  try {
    window.localStorage.setItem(SOLO_BEST_KEY, String(score));
  } catch {
    /* ignore */
  }
};

function snapAnnouncement(
  outcome: SnapOutcome,
  name: string,
  formula: string,
  bond: BondType,
  points: number,
): string {
  const bondLabel = BOND_LABEL[bond].toLowerCase();
  switch (outcome) {
    case "fast":
      return `Correct - ${name}, ${formula}, is ${bondLabel}. Fast bonus, plus ${points} points.`;
    case "slow":
      return `Correct - ${name}, ${formula}, is ${bondLabel}. Plus ${points} points.`;
    case "wrong":
      return `Incorrect. ${name}, ${formula}, is ${bondLabel}.`;
    case "timeout":
      return `Time's up. ${name}, ${formula}, is ${bondLabel}.`;
  }
}

/**
 * Bond Type Snap - snap-judge each compound as ionic, covalent, or
 * metallic. Fast time bonus encourages instinct over deliberation.
 */
export default function BondTypeSnap() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode | null>(null);

  return (
    <>
      <LiveAnnouncer />
      {!mode ? (
        <ModeSelect onPick={setMode} />
      ) : mode === "solo" ? (
        <SoloMatch onChangeMode={() => setMode(null)} onExit={() => navigate("/play")} />
      ) : (
        <HotSeatMatch onChangeMode={() => setMode(null)} onExit={() => navigate("/play")} />
      )}
    </>
  );
}

// ---------------- mode select ----------------

function ModeSelect({ onPick }: { onPick: (m: Mode) => void }) {
  return (
    <GameLayout
      title="Bond Type Snap"
      subtitle="Ionic, covalent, or metallic? Snap-judge each compound before time runs out."
      headerActions={<GameMenu gameKey="bond-type-snap" />}
    >
      <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <Link2 size={16} className="text-primary" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Choose a mode</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Solo for personal best, or hot-seat to compete head-to-head.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <ModeButton
            icon={<User size={14} className="text-primary" aria-hidden="true" />}
            label="Solo"
            blurb="One player. Best score saved."
            onClick={() => onPick("solo")}
          />
          <ModeButton
            icon={<Users size={14} className="text-primary" aria-hidden="true" />}
            label="Hot-seat 2P"
            blurb="Alternate questions. Highest total wins."
            onClick={() => onPick("hot-seat")}
          />
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          {SNAP_TURN_SECONDS}s per question · ≤{SNAP_FAST_THRESHOLD_S}s ={" "}
          {SNAP_POINTS_FAST} pts · slower correct = {SNAP_POINTS_SLOW} pts.
        </p>
      </div>
    </GameLayout>
  );
}

function ModeButton({
  icon,
  label,
  blurb,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  blurb: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-semibold text-foreground">{label}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-snug">{blurb}</p>
    </button>
  );
}

// ---------------- solo ----------------

type SoloPhase =
  | { kind: "playing"; questionIndex: number; question: SnapQuestion; startedAt: number; score: number }
  | {
      kind: "result";
      questionIndex: number;
      question: SnapQuestion;
      chosen: BondType | null;
      score: number;
      lastAward: number;
      outcome: SnapOutcome;
    }
  | { kind: "finished"; score: number; best: number };

function makeFirstQuestion(score: number): SoloPhase {
  const q = generateQuestion(Math.random, new Set());
  return {
    kind: "playing",
    questionIndex: 0,
    question: q,
    startedAt: performance.now(),
    score,
  };
}

function SoloMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const [phase, setPhase] = useState<SoloPhase>(() => makeFirstQuestion(0));
  const askedRef = useRef<Set<string>>(new Set());
  const historyRef = useRef<HistoryEntry[]>([]);
  const advanceTimer = useRef<number | null>(null);
  const announce = useAnnouncer();

  useEffect(() => {
    if (phase.kind === "playing") askedRef.current.add(phase.question.id);
  }, [phase]);
  useEffect(() => () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
  }, []);

  const handleAnswer = useCallback(
    (chosen: BondType | null) => {
      if (phase.kind !== "playing") return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreAnswer(chosen, phase.question, elapsed);
      const nextScore = phase.score + points;
      const c = phase.question.compound;
      historyRef.current.push({
        id: c.id,
        name: c.name,
        formula: c.formula,
        bond: c.bond,
        why: c.why,
        note: c.note,
        outcome,
      });
      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");
      announce(
        snapAnnouncement(outcome, c.name, c.formula, c.bond, points),
        outcome === "timeout" ? "assertive" : "polite",
      );

      setPhase({
        kind: "result",
        questionIndex: phase.questionIndex,
        question: phase.question,
        chosen,
        score: nextScore,
        lastAward: points,
        outcome,
      });

      advanceTimer.current = window.setTimeout(() => {
        const nextIndex = phase.questionIndex + 1;
        if (nextIndex >= QUESTIONS_PER_MATCH) {
          const best = readBest();
          if (nextScore > best) writeBest(nextScore);
          play(nextScore > best ? "win" : "click");
          setPhase({ kind: "finished", score: nextScore, best: Math.max(best, nextScore) });
          return;
        }
        const nextQ = generateQuestion(Math.random, askedRef.current);
        askedRef.current.add(nextQ.id);
        setPhase({
          kind: "playing",
          questionIndex: nextIndex,
          question: nextQ,
          startedAt: performance.now(),
          score: nextScore,
        });
      }, RESULT_PAUSE_MS);
    },
    [phase],
  );

  const onExpire = useCallback(() => handleAnswer(null), [handleAnswer]);

  const restart = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    setPhase(makeFirstQuestion(0));
  };

  if (phase.kind === "finished") {
    return (
      <GameLayout
        title="Bond Type Snap"
        subtitle="Solo · 15 questions"
        headerActions={<GameMenu gameKey="bond-type-snap" onReset={restart} resetLabel="Restart" />}
      >
        <MatchRecap
          history={historyRef.current}
          stat={
            <div className="text-center">
              <Trophy size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">Final score</p>
              <p className="text-4xl font-bold font-mono text-foreground tabular-nums mb-1">
                {phase.score}
              </p>
              <p className="text-xs text-muted-foreground">
                Personal best: <span className="font-mono">{phase.best}</span>
              </p>
            </div>
          }
          onExit={onExit}
          onChangeMode={onChangeMode}
          onAgain={restart}
          againLabel="Play again"
        />
      </GameLayout>
    );
  }

  const active = phase.kind === "playing" ? phase : null;
  const result = phase.kind === "result" ? phase : null;
  const q = active?.question ?? result!.question;
  const idx = active?.questionIndex ?? result!.questionIndex;
  const score = active?.score ?? result!.score;

  return (
    <GameLayout
      title="Bond Type Snap"
      subtitle="Solo · 15 questions"
      headerActions={<GameMenu gameKey="bond-type-snap" onReset={restart} resetLabel="Restart" />}
    >
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Question" value={`${idx + 1} / ${QUESTIONS_PER_MATCH}`} />
        <Stat label="Score" value={String(score)} mono />
        <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Time</span>
          {active ? (
            <TurnTimer
              key={`solo-${q.id}-${idx}`}
              seconds={SNAP_TURN_SECONDS}
              onExpire={onExpire}
              size={40}
            />
          ) : (
            <Timer size={18} className="text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </div>

      <QuestionCard
        question={q}
        chosen={result?.chosen ?? null}
        onChoose={(b) => active && handleAnswer(b)}
        revealed={!!result}
      />

      {result && (
        <ResultBanner
          outcome={result.outcome}
          award={result.lastAward}
          name={q.compound.name}
          formula={q.compound.formula}
          bond={q.compound.bond}
          why={q.compound.why}
          note={q.compound.note}
        />
      )}
    </GameLayout>
  );
}

// ---------------- hot-seat ----------------

type HotSeatPhase =
  | { kind: "answering"; question: SnapQuestion; startedAt: number }
  | {
      kind: "result";
      question: SnapQuestion;
      chosen: BondType | null;
      lastAward: number;
      outcome: SnapOutcome;
    }
  | { kind: "handoff"; nextPlayer: PlayerId };

function HotSeatMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const match = useHotSeatMatch(0);
  const [phase, setPhase] = useState<HotSeatPhase | null>(null);
  const askedRef = useRef<Set<string>>(new Set());
  const historyRef = useRef<HistoryEntry[]>([]);
  const advanceTimer = useRef<number | null>(null);
  const announce = useAnnouncer();
  const TOTAL_TURNS = 10 * 2; // 10 rounds each

  useEffect(() => () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
  }, []);

  const startMatch = useCallback(
    (p1: string, p2: string) => {
      askedRef.current = new Set();
      historyRef.current = [];
      match.start(p1, p2);
      setPhase({ kind: "handoff", nextPlayer: 0 });
    },
    [match],
  );

  const beginTurn = useCallback(() => {
    const q = generateQuestion(Math.random, askedRef.current);
    askedRef.current.add(q.id);
    setPhase({ kind: "answering", question: q, startedAt: performance.now() });
  }, []);

  const submitAnswer = useCallback(
    (chosen: BondType | null) => {
      if (!phase || phase.kind !== "answering") return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreAnswer(chosen, phase.question, elapsed);

      const current = match.state.currentTurn;
      const newScore = match.state.stats[current] + points;
      match.setStat(current, newScore);

      const c = phase.question.compound;
      historyRef.current.push({
        id: c.id,
        name: c.name,
        formula: c.formula,
        bond: c.bond,
        why: c.why,
        note: c.note,
        outcome,
      });

      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");
      announce(
        snapAnnouncement(outcome, c.name, c.formula, c.bond, points),
        outcome === "timeout" ? "assertive" : "polite",
      );

      setPhase({ kind: "result", question: phase.question, chosen, lastAward: points, outcome });

      advanceTimer.current = window.setTimeout(() => {
        if (match.state.turnNumber >= TOTAL_TURNS) {
          const a = match.state.stats[0] + (current === 0 ? points : 0);
          const b = match.state.stats[1] + (current === 1 ? points : 0);
          const winner: PlayerId | null = a === b ? null : a > b ? 0 : 1;
          play(winner === null ? "click" : "win");
          match.finish(winner);
          return;
        }
        match.nextTurn();
        const next: PlayerId = current === 0 ? 1 : 0;
        setPhase({ kind: "handoff", nextPlayer: next });
      }, RESULT_PAUSE_MS);
    },
    [phase, match, TOTAL_TURNS],
  );

  const onExpire = useCallback(() => submitAnswer(null), [submitAnswer]);

  const handleRematch = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.start(match.state.players[0].name, match.state.players[1].name);
    setPhase({ kind: "handoff", nextPlayer: 0 });
  };
  const handleChangePlayers = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.reset();
    setPhase(null);
  };

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Bond Type Snap"
        subtitle="Hot-seat · 10 rounds each"
        headerActions={<GameMenu gameKey="bond-type-snap" />}
      >
        <PlayerSetup
          title="Set up the match"
          description="Two players alternate compounds on the same device. Highest total wins."
          ctaLabel="Start"
          onStart={startMatch}
        />
        <div className="max-w-md mx-auto mt-3 text-center">
          <button
            type="button"
            onClick={onChangeMode}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Change mode
          </button>
        </div>
      </GameLayout>
    );
  }

  const matchOver = match.state.status === "finished";
  const current = match.state.players[match.state.currentTurn];
  const turnsRemaining = Math.max(0, TOTAL_TURNS - match.state.turnNumber + 1);

  return (
    <GameLayout
      title="Bond Type Snap"
      subtitle="Hot-seat 2P"
      headerActions={<GameMenu gameKey="bond-type-snap" onReset={handleRematch} resetLabel="Restart" />}
    >
      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="Score" />
      </div>

      {phase?.kind === "handoff" && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">Pass the device to:</p>
          <p className="text-2xl font-bold text-foreground mb-1">{current.name}</p>
          <p className="text-[11px] text-muted-foreground mb-4">
            Turn {match.state.turnNumber} of {TOTAL_TURNS} · {turnsRemaining} left
          </p>
          <Button onClick={beginTurn} size="lg">
            <Link2 size={14} className="mr-1.5" aria-hidden="true" />
            I'm ready
          </Button>
        </div>
      )}

      {phase?.kind === "answering" && !matchOver && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-muted-foreground">{current.name}'s turn</span>
            <TurnTimer
              key={`hs-${phase.question.id}-${match.state.turnNumber}`}
              seconds={SNAP_TURN_SECONDS}
              onExpire={onExpire}
              size={44}
            />
          </div>
          <QuestionCard question={phase.question} chosen={null} onChoose={submitAnswer} revealed={false} />
        </>
      )}

      {phase?.kind === "result" && !matchOver && (
        <>
          <QuestionCard
            question={phase.question}
            chosen={phase.chosen}
            onChoose={() => undefined}
            revealed
          />
          <ResultBanner
            outcome={phase.outcome}
            award={phase.lastAward}
            name={phase.question.compound.name}
            formula={phase.question.compound.formula}
            bond={phase.question.compound.bond}
            why={phase.question.compound.why}
            note={phase.question.compound.note}
          />
        </>
      )}

      <MatchResultDialog
        state={match.state}
        open={matchOver}
        onRematch={handleRematch}
        onChangePlayers={handleChangePlayers}
        onExit={onExit}
        detail="10 rounds each. Higher total wins."
      />
    </GameLayout>
  );
}

// ---------------- shared sub-components ----------------

function Stat({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
        {label}
      </div>
      <div
        className={`text-base font-semibold text-foreground ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function QuestionCard({
  question,
  chosen,
  onChoose,
  revealed,
}: {
  question: SnapQuestion;
  chosen: BondType | null;
  onChoose: (b: BondType) => void;
  revealed: boolean;
}) {
  const correct = question.compound.bond;
  const groupRef = useRef<HTMLDivElement | null>(null);

  // Number-key shortcuts: 1=ionic, 2=covalent, 3=metallic.
  useEffect(() => {
    if (revealed) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      )
        return;
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= BOND_OPTIONS.length) {
        e.preventDefault();
        onChoose(BOND_OPTIONS[n - 1]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [revealed, onChoose]);

  const onChoiceKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    idx: number,
  ) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      if (!revealed) onChoose(BOND_OPTIONS[idx]);
      return;
    }
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End")
      return;
    e.preventDefault();
    const root = groupRef.current;
    if (!root) return;
    const buttons = Array.from(
      root.querySelectorAll<HTMLButtonElement>("button[data-bond]"),
    );
    if (!buttons.length) return;
    let next = idx;
    if (e.key === "ArrowRight") next = (idx + 1) % buttons.length;
    else if (e.key === "ArrowLeft") next = (idx - 1 + buttons.length) % buttons.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = buttons.length - 1;
    buttons[next]?.focus();
  };

  return (
    <div className="rounded-xl border border-primary/40 bg-card p-5 mb-3">
      <div className="flex flex-col items-center mb-4 text-center">
        <Badge variant="outline" className="text-[10px] mb-2">
          Compound
        </Badge>
        <h2 className="text-2xl font-mono font-semibold text-foreground mb-1">
          {question.compound.formula}
        </h2>
        <p className="text-xs text-muted-foreground">{question.compound.name}</p>
        <p className="sr-only">
          Choose the bond type. Use number keys 1, 2, or 3, or arrow keys.
        </p>
      </div>

      <div
        ref={groupRef}
        className="grid grid-cols-1 sm:grid-cols-3 gap-2"
        role="group"
        aria-label={`Bond type choices for ${question.compound.name}, formula ${question.compound.formula}`}
      >
        {BOND_OPTIONS.map((bond, idx) => {
          const isCorrect = bond === correct;
          const isChosen = bond === chosen;
          const stateClasses = revealed
            ? isCorrect
              ? "border-primary bg-primary/10 text-foreground"
              : isChosen
                ? "border-destructive/60 bg-destructive/10 text-foreground"
                : "border-border bg-background text-muted-foreground"
            : "border-border bg-background hover:border-primary/60 hover:bg-accent/40 text-foreground";
          const stateLabel = revealed
            ? isCorrect
              ? ", correct answer"
              : isChosen
                ? ", your answer (incorrect)"
                : ""
            : "";
          return (
            <button
              key={bond}
              type="button"
              data-bond={bond}
              onClick={() => !revealed && onChoose(bond)}
              onKeyDown={(e) => onChoiceKeyDown(e, idx)}
              disabled={revealed}
              aria-label={`${idx + 1}. ${BOND_LABEL[bond]}${stateLabel}`}
              aria-pressed={isChosen}
              className={`text-center p-4 rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${stateClasses} disabled:cursor-default`}
            >
              <span className="text-sm font-semibold">{BOND_LABEL[bond]}</span>
              <span className="sr-only"> (press {idx + 1})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultBanner({
  outcome,
  award,
  name,
  formula,
  bond,
  why,
  note,
}: {
  outcome: SnapOutcome;
  award: number;
  name: string;
  formula: string;
  bond: BondType;
  why: string;
  note?: string;
}) {
  const positive = outcome === "fast" || outcome === "slow";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-md border p-3 ${
        positive ? "border-primary/40 bg-primary/5" : "border-destructive/30 bg-destructive/5"
      }`}
    >
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {positive ? (
          <CheckCircle2 size={14} className="text-primary" aria-hidden="true" />
        ) : (
          <XCircle size={14} className="text-destructive" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {outcome === "fast" && `Fast & correct! +${award} pts`}
          {outcome === "slow" && `Correct. +${award} pts`}
          {outcome === "wrong" && `Not quite - ${name} (${formula}) is ${BOND_LABEL[bond].toLowerCase()}.`}
          {outcome === "timeout" && `Time's up - ${name} (${formula}) is ${BOND_LABEL[bond].toLowerCase()}.`}
        </span>
        <Badge variant="secondary" className="text-[10px] ml-auto">
          {BOND_LABEL[bond]}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{why}</p>
      {note && (
        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-muted-foreground bg-muted/30 rounded px-2 py-1.5 border border-border/50">
          <Info size={11} className="text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{note}</span>
        </div>
      )}
      <WhyCorrect text={`${why}${note ? "\n\n" + note : ""}`} defaultOpen={!positive} />
    </div>
  );
}

function WhyCorrect({ text, defaultOpen = false }: { text: string; defaultOpen?: boolean }) {
  return (
    <details
      className="mt-2 group rounded-md border border-border/60 bg-background/40 open:bg-background/60"
      open={defaultOpen}
    >
      <summary className="cursor-pointer list-none px-2.5 py-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
        <BookOpen size={12} className="text-primary" aria-hidden="true" />
        <span>Why this is correct</span>
        <ChevronDown
          size={12}
          className="ml-auto text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <p className="px-2.5 pb-2.5 pt-0.5 text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
        {text}
      </p>
    </details>
  );
}

// ---------------- recap ----------------

function MatchRecap({
  stat,
  history,
  onExit,
  onChangeMode,
  onAgain,
  againLabel,
}: {
  stat: React.ReactNode;
  history: HistoryEntry[];
  onExit: () => void;
  onChangeMode: () => void;
  onAgain: () => void;
  againLabel: string;
}) {
  const total = history.length;
  const correct = history.filter((h) => h.outcome === "fast" || h.outcome === "slow").length;
  const wrong = history.filter((h) => h.outcome === "wrong").length;
  const timeout = history.filter((h) => h.outcome === "timeout").length;
  const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

  const byBond = useMemo(() => {
    const map = new Map<BondType, { c: number; t: number }>();
    for (const h of history) {
      const cur = map.get(h.bond) ?? { c: 0, t: 0 };
      cur.t += 1;
      if (h.outcome === "fast" || h.outcome === "slow") cur.c += 1;
      map.set(h.bond, cur);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].t - a[1].t);
  }, [history]);

  const topLessons = useMemo(() => {
    const seen = new Set<string>();
    const lessons: HistoryEntry[] = [];
    for (const h of history) {
      if (h.outcome === "wrong" || h.outcome === "timeout") {
        if (seen.has(h.id)) continue;
        seen.add(h.id);
        lessons.push(h);
        if (lessons.length >= 3) return lessons;
      }
    }
    for (const h of history) {
      if (lessons.length >= 3) break;
      if (seen.has(h.id)) continue;
      seen.add(h.id);
      lessons.push(h);
    }
    return lessons;
  }, [history]);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="rounded-xl border border-border bg-card p-6">{stat}</div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold text-foreground mb-3">Performance</h3>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <BreakdownStat label="Correct" value={correct} tone="positive" />
          <BreakdownStat label="Wrong" value={wrong} tone="negative" />
          <BreakdownStat label="Timeout" value={timeout} tone="muted" />
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Accuracy
            </span>
            <span className="text-sm font-mono tabular-nums text-foreground">{accuracy}%</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${accuracy}%` }} />
          </div>
        </div>

        {byBond.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {byBond.map(([bond, { c, t }]) => (
              <div
                key={bond}
                className="flex items-center justify-between text-xs px-3 py-2 rounded-md border border-border bg-background"
              >
                <span className="text-muted-foreground">{BOND_LABEL[bond]}</span>
                <span className="font-mono tabular-nums text-foreground">
                  {c}/{t}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {topLessons.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={14} className="text-primary" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-foreground">Top lessons</h3>
          </div>
          <ul className="space-y-2">
            {topLessons.map((l) => (
              <li key={l.id} className="rounded-md border border-border bg-background p-3">
                <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-semibold text-foreground">
                    {l.name} <span className="text-muted-foreground font-mono">({l.formula})</span>
                  </span>
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
                    {BOND_LABEL[l.bond]}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{l.why}</p>
                {l.note && (
                  <p className="mt-1 text-[11px] text-muted-foreground italic">{l.note}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Button variant="outline" onClick={onExit}>
          <LogOut size={14} className="mr-1.5" aria-hidden="true" />
          Quit
        </Button>
        <Button variant="outline" onClick={onChangeMode}>
          Change mode
        </Button>
        <Button onClick={onAgain}>
          <RotateCcw size={14} className="mr-1.5" aria-hidden="true" />
          {againLabel}
        </Button>
      </div>
    </div>
  );
}

function BreakdownStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "positive" | "negative" | "muted";
}) {
  const toneClass =
    tone === "positive"
      ? "text-primary"
      : tone === "negative"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <div className="rounded-md border border-border bg-background p-3 text-center">
      <div className={`text-2xl font-bold font-mono tabular-nums ${toneClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
        {label}
      </div>
    </div>
  );
}